import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import { PageModuleStatus } from "entities/pages/types";
import React, { useState, MouseEvent, useRef } from "react";
import Chip from "shared/components/Chip";
import { StyledMenu } from "./styled";

export type StatusBadgeBaseProps = {
  status: PageModuleStatus;
  title: string;
  color: string;
  id: string;
  readOnly?: boolean;
};

export type StatusBadgeProps<
  T extends StatusBadgeBaseProps,
  P extends Record<string, unknown> = {},
> = {
  item: T;
  handleStatus?: (
    params: {
      id: string;
      status: PageModuleStatus;
    } & P,
  ) => void;
  readOnly?: boolean;
  additionalParams?: P;
  statusOptions?: Array<{
    status: PageModuleStatus;
    label: string;
    isDisabled?: (item: T) => boolean;
  }>;
};

function StatusBadge<
  T extends StatusBadgeBaseProps,
  P extends Record<string, unknown> = {},
>({
  item,
  handleStatus,
  readOnly = false,
  additionalParams = {} as P,
  statusOptions = [
    {
      status: PageModuleStatus.Active,
      label: "Active",
      isDisabled: (item: T) => item.status === PageModuleStatus.Active,
    },
    {
      status: PageModuleStatus.Draft,
      label: "Inactive",
      isDisabled: (item: T) =>
        item.status !== PageModuleStatus.Active &&
        item.status !== PageModuleStatus.Expired,
    },
  ],
}: Readonly<StatusBadgeProps<T, P>>) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const chipRef = useRef<HTMLDivElement>(null);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    if (readOnly) return;
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(null);
    if (chipRef.current) {
      chipRef.current.focus();
    }
  };

  const handleStatusChange =
    (newStatus: PageModuleStatus) => (event: MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      if (handleStatus) {
        handleStatus({
          id: item.id,
          status: newStatus,
          ...additionalParams,
        } as {
          id: string;
          status: PageModuleStatus;
        } & P);
      }
      setAnchorEl(null);
      if (chipRef.current) {
        chipRef.current.focus();
      }
    };

  return (
    <Box>
      <Box
        ref={chipRef}
        onClick={handleClick}
        sx={{
          cursor: readOnly ? "default" : "pointer",
        }}
        tabIndex={readOnly ? -1 : 0}
        role={readOnly ? undefined : "button"}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Chip
          title={item.title}
          bgColor={item.color}
          withDropdownArrow={!readOnly}
          isOpen={open}
        />
      </Box>

      {!readOnly && (
        <StyledMenu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "status-badge-button",
            role: "menu",
          }}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
          {statusOptions.map((option) => (
            <MenuItem
              key={option.status}
              onClick={handleStatusChange(option.status)}
              disabled={option.isDisabled ? option.isDisabled(item) : false}
              role="menuitem"
            >
              <Typography variant="bodySRegular">{option.label}</Typography>
            </MenuItem>
          ))}
        </StyledMenu>
      )}
    </Box>
  );
}

export default StatusBadge;
